import { ActiveCell, DefaultBadge } from "@/components/commerce/grid-cells";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { PaymentService } from "@/gen/omnios/v1/payment_connect";
import { transport } from "@/lib/grpc";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { SectionNav } from "@/components/commerce/section-nav";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";

import type {
	ColDef,
	SelectionChangedEvent,
} from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { useServerGrid } from "@/lib/use-server-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowDownUp,
	CreditCard,
	Loader2,
	Pencil,
	Plus,
	X,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/commerce/payments")({
	component: PaymentsPage,
});

const paymentClient = createClient(PaymentService, transport);

type Section = "methods" | "transactions";

function PaymentsPage() {
	const { t } = useTranslation("commerce");

	const NAV_ITEMS: {
		value: Section;
		label: string;
		icon: typeof CreditCard;
	}[] = [
		{ value: "methods", label: t("pay_nav_methods"), icon: CreditCard },
		{ value: "transactions", label: t("pay_nav_transactions"), icon: ArrowDownUp },
	];

	useBreadcrumbs([{ label: t("payments") }]);
	const [section, setSection] = useState<Section>("methods");

	return (
		<div className="flex h-full overflow-hidden">
			<SectionNav items={NAV_ITEMS} value={section} onChange={setSection} />

			<div className="flex-1 flex flex-col overflow-hidden">
				{section === "methods" && <PaymentMethodsPanel />}
				{section === "transactions" && <TransactionsPanel />}
			</div>
		</div>
	);
}

// -- Payment Methods ----------------------------------------

interface MethodForm {
	displayName: string;
	providerSlug: string;
	supportedCurrencies: string[];
	configJson: string;
	sortOrder: number;
}

const emptyMethodForm: MethodForm = {
	displayName: "",
	providerSlug: "stripe",
	supportedCurrencies: ["RUB"],
	configJson: "{}",
	sortOrder: 0,
};

function PaymentMethodsPanel() {
	const { t } = useTranslation("commerce");
	const qc = useQueryClient();
	const gridRef = useRef<AgGridReact>(null);
	const { onGridReady, onStateChanged } = useGridLayout("payment-methods");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [showDialog, setShowDialog] = useState(false);
	const [editId, setEditId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const [form, setForm] = useState<MethodForm>(emptyMethodForm);

	const { data, isLoading } = useQuery({
		queryKey: ["payment-methods"],
		queryFn: () => paymentClient.listPaymentMethods({ activeOnly: false }),
	});

	const createMut = useMutation({
		mutationFn: () => {
			let config: Record<string, unknown> | undefined;
			try {
				config = JSON.parse(form.configJson);
			} catch {
				config = {};
			}
			return paymentClient.configurePaymentMethod({
				displayName: form.displayName,
				providerSlug: form.providerSlug,
				supportedCurrencies: form.supportedCurrencies,
				config: config as any,
				sortOrder: form.sortOrder,
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["payment-methods"] });
			closeDialog();
		},
	});

	const updateMut = useMutation({
		mutationFn: () => {
			let config: Record<string, unknown> | undefined;
			try {
				config = JSON.parse(form.configJson);
			} catch {
				config = {};
			}
			return paymentClient.updatePaymentMethod({
				id: editId!,
				displayName: form.displayName,
				config: config as any,
				sortOrder: form.sortOrder,
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["payment-methods"] });
			closeDialog();
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => paymentClient.deletePaymentMethod({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["payment-methods"] });
			setDeleteTarget(null);
		},
	});

	function openCreate() {
		setEditId(null);
		setForm(emptyMethodForm);
		setShowDialog(true);
	}

	function openEdit(m: any) {
		setEditId(m.id);
		setForm({
			displayName: m.displayName,
			providerSlug: m.providerSlug,
			supportedCurrencies: m.supportedCurrencies ?? [],
			configJson: "{}",
			sortOrder: m.sortOrder ?? 0,
		});
		setShowDialog(true);
	}

	function closeDialog() {
		setShowDialog(false);
		setEditId(null);
		setForm(emptyMethodForm);
	}

	const CURRENCIES = ["RUB", "USD", "EUR"];

	const methods = useMemo(() => data?.methods ?? [], [data]);

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "displayName",
				headerName: t("pay_col_name"),
				flex: 1,
				minWidth: 150,
			},
			{
				field: "providerSlug",
				headerName: t("pay_col_provider"),
				width: 120,
			},
			{
				field: "isActive",
				headerName: t("pay_col_active"),
				width: 90,
				cellRenderer: (p: { value: boolean; data: any }) => (
					<ActiveCell value={p.value} activeLabel={t("pay_col_active")} />
				),
			},
			{
				field: "isDefault",
				headerName: t("pay_col_default"),
				width: 100,
				cellRenderer: (p: { value: boolean }) => {
					if (!p.value) return null;
					return <DefaultBadge label={t("pay_default")} />;
				},
			},
			{
				field: "sortOrder",
				headerName: t("pay_col_sort"),
				width: 80,
				type: "rightAligned",
				cellClass: "font-tabular",
			},
			{
				colId: "actions",
				headerName: "",
				width: 80,
				suppressHeaderMenuButton: true,
				sortable: false,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return null;
					return (
						<div className="flex gap-1 justify-end">
							<button type="button" className="cursor-pointer bg-transparent border-none text-muted-foreground p-0.5" title={t("common:edit", { defaultValue: "Ред." })} onClick={() => openEdit(p.data)}><Pencil className="h-3.5 w-3.5" /></button>
							<button type="button" className="cursor-pointer bg-transparent border-none text-muted-foreground p-0.5" title={t("common:delete", { defaultValue: "Уд." })} onClick={() => setDeleteTarget(p.data.id)}><X className="h-3.5 w-3.5" /></button>
						</div>
					);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onSelectionChanged = useCallback(
		(e: SelectionChangedEvent) => {
			setSelectedRows(e.api.getSelectedRows());
		},
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${methods.length} ${t("pay_nav_methods", { defaultValue: "\u043C\u0435\u0442\u043E\u0434\u043E\u0432" })}`,
				align: "left" as const,
			},
		],
		[methods.length, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			<div className="flex items-center gap-2 px-4 py-2 shrink-0 justify-end">
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() =>
						gridRef.current?.api?.exportDataAsCsv({
							fileName: "payment-methods.csv",
						})
					}
				>
					{t("common:export", { defaultValue: "Экспорт" })}
				</Button>
				<Button size="sm" className="h-9" onClick={openCreate}>
					<Plus className="h-4 w-4 mr-1" />
					{t("common:create")}
				</Button>
			</div>
			<div
				className="flex-1 ag-theme-quartz h-full"
				style={{ width: "100%" }}
			>
				<AgGridReact
					ref={gridRef}
					rowData={methods}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={onStateChanged}
					onFilterChanged={onStateChanged}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data.id}
					loading={isLoading}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("pay_methods_empty", { defaultValue: "\u041D\u0435\u0442 \u043C\u0435\u0442\u043E\u0434\u043E\u0432 \u043E\u043F\u043B\u0430\u0442\u044B" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>

			{/* Create / Edit Dialog */}
			<Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{editId ? t("pay_edit_method") : t("pay_new_method")}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("pay_col_name")}</Label>
							<Input
								value={form.displayName}
								onChange={(e) =>
									setForm({ ...form, displayName: e.target.value })
								}
								placeholder={t("pay_name_placeholder")}
							/>
						</div>
						{!editId && (
							<div className="space-y-2">
								<Label>{t("pay_col_provider")}</Label>
								<Select
									value={form.providerSlug}
									onValueChange={(v) =>
										setForm({ ...form, providerSlug: v })
									}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="stripe">Stripe</SelectItem>
										<SelectItem value="yookassa">{"\u042E"}Kassa</SelectItem>
										<SelectItem value="tinkoff">{"\u0422\u0438\u043D\u044C\u043A\u043E\u0444\u0444"}</SelectItem>
										<SelectItem value="manual">
											{t("pay_provider_manual")}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}
						{!editId && (
							<div className="space-y-2">
								<Label>{t("pay_currencies")}</Label>
								<div className="flex gap-3">
									{CURRENCIES.map((cur) => (
										<Label key={cur} className="flex items-center gap-2 text-sm">
											<Checkbox
												checked={form.supportedCurrencies.includes(cur)}
												onCheckedChange={(checked) => {
													const next = checked
														? [...form.supportedCurrencies, cur]
														: form.supportedCurrencies.filter((c) => c !== cur);
													setForm({ ...form, supportedCurrencies: next });
												}}
											/>
											{cur}
										</Label>
									))}
								</div>
							</div>
						)}
						<div className="space-y-2">
							<Label>{t("pay_config_json")}</Label>
							<Textarea
								value={form.configJson}
								onChange={(e) =>
									setForm({ ...form, configJson: e.target.value })
								}
								rows={4}
								className="font-mono text-xs"
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pay_col_sort")}</Label>
							<Input
								type="number"
								value={form.sortOrder}
								onChange={(e) =>
									setForm({ ...form, sortOrder: Number(e.target.value) })
								}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDialog}>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() =>
								editId ? updateMut.mutate() : createMut.mutate()
							}
							disabled={
								!form.displayName ||
								createMut.isPending ||
								updateMut.isPending
							}
						>
							{(createMut.isPending || updateMut.isPending) && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{editId ? t("common:save") : t("common:create")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Delete Confirm */}
			<AlertDialog
				open={!!deleteTarget}
				onOpenChange={(o) => !o && setDeleteTarget(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("pay_delete_method")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("pay_action_irreversible")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => deleteTarget && deleteMut.mutate(deleteTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}

// -- Transactions -------------------------------------------

const TX_STATUSES = [
	"pending",
	"authorized",
	"captured",
	"refunded",
	"failed",
	"cancelled",
] as const;

function statusColor(s: string): string {
	switch (s) {
		case "captured":
			return "hsl(var(--chart-2))";
		case "authorized":
			return "hsl(var(--chart-1))";
		case "refunded":
			return "hsl(var(--chart-4))";
		case "failed":
		case "cancelled":
			return "hsl(var(--destructive))";
		default:
			return "hsl(var(--muted-foreground))";
	}
}

function TransactionsPanel() {
	const { t } = useTranslation("commerce");
	const { onGridReady, onStateChanged } = useGridLayout("payment-transactions");
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [statusFilter, setStatusFilter] = useState<string>("");
	const [captureTarget, setCaptureTarget] = useState<any | null>(null);
	const [refundTarget, setRefundTarget] = useState<any | null>(null);
	const [captureAmount, setCaptureAmount] = useState(0);
	const [refundAmount, setRefundAmount] = useState(0);
	const [refundReason, setRefundReason] = useState("");

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<any>({
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await paymentClient.listTransactions({
				status: statusFilter,
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.transactions,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter: statusFilter,
		colToField: {
			createdAt: "created_at",
		},
	});

	const captureMut = useMutation({
		mutationFn: () =>
			paymentClient.capturePayment({
				transactionId: captureTarget!.id,
				amountCents: BigInt(captureAmount),
			}),
		onSuccess: () => {
			refreshGrid();
			setCaptureTarget(null);
		},
	});

	const refundMut = useMutation({
		mutationFn: () =>
			paymentClient.refundPayment({
				transactionId: refundTarget!.id,
				amountCents: BigInt(refundAmount),
				reason: refundReason,
			}),
		onSuccess: () => {
			refreshGrid();
			setRefundTarget(null);
		},
	});

	function openCapture(tx: any) {
		setCaptureTarget(tx);
		setCaptureAmount(Number(tx.amountCents));
	}

	function openRefund(tx: any) {
		setRefundTarget(tx);
		setRefundAmount(Number(tx.amountCents));
		setRefundReason("");
	}

	const columnDefs = useMemo<ColDef[]>(
		() => [
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 36,
				maxWidth: 36,
				pinned: "left",
				suppressHeaderMenuButton: true,
				resizable: false,
			},
			{
				field: "id",
				headerName: "ID",
				width: 100,
				pinned: "left",
				cellClass: "font-mono",
				valueFormatter: (p) => p.value?.slice(0, 8) ?? "",
			},
			{
				field: "orderId",
				headerName: t("pay_col_order"),
				width: 120,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return "\u2014";
					return (
						<a href={`/commerce/orders/${p.value}`} style={{ color: "var(--primary)", fontSize: 12, textDecoration: "none" }}
							onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
							onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}>
							{p.value.slice(0, 8)}...
						</a>
					);
				},
			},
			{
				colId: "amount",
				headerName: t("pay_col_amount"),
				width: 110,
				type: "rightAligned",
				cellClass: "font-tabular",
				filter: "agNumberColumnFilter",
				valueGetter: (p) => Number(p.data?.amountCents ?? 0) / 100,
				valueFormatter: (p) => p.value?.toFixed(2) ?? "\u2014",
			},
			{
				field: "currency",
				headerName: t("pay_col_currency"),
				width: 70,
			},
			{
				field: "status",
				headerName: t("pay_col_status"),
				width: 120,
				filter: false,
				cellRenderer: (p: { value: string }) => {
					if (!p.value) return "\u2014";
					const color = statusColor(p.value);
					const label = t(`pay_status_${p.value}`, { defaultValue: p.value });
					return (
						<span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 4, fontSize: 11, lineHeight: "18px", background: `color-mix(in srgb, ${color} 10%, transparent)`, color }}>
							{label}
						</span>
					);
				},
			},
			{
				field: "providerReference",
				headerName: t("pay_col_provider_ref"),
				flex: 1,
				minWidth: 100,
				valueFormatter: (p) => p.value || "\u2014",
			},
			{
				field: "createdAt",
				headerName: t("pay_col_created"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p) => {
					const ts = p.data?.createdAt;
					return ts
						? new Date(Number(ts.seconds) * 1000).toLocaleString("ru-RU")
						: "\u2014";
				},
			},
			{
				colId: "actions",
				headerName: "",
				width: 90,
				suppressHeaderMenuButton: true,
				sortable: false,
				filter: false,
				cellRenderer: (p: { data: any }) => {
					if (!p.data) return "";
					if (p.data.status === "authorized")
						return (
							<button data-action="capture" style={{ cursor: "pointer", background: "none", border: "none", color: "var(--primary)", fontSize: 12, padding: "2px 4px" }}>
								{t("pay_action_capture")}
							</button>
						);
					if (p.data.status === "captured")
						return (
							<button data-action="refund" style={{ cursor: "pointer", background: "none", border: "none", color: "var(--primary)", fontSize: 12, padding: "2px 4px" }}>
								{t("pay_action_refund")}
							</button>
						);
					return "";
				},
				onCellClicked: (e) => {
					const target = e.event?.target as HTMLElement | undefined;
					const action = target?.closest("[data-action]")?.getAttribute("data-action");
					if (action === "capture") openCapture(e.data);
					else if (action === "refund") openRefund(e.data);
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			suppressHeaderMenuButton: false,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const onSelectionChanged = useCallback(
		(e: SelectionChangedEvent) => {
			setSelectedRows(e.api.getSelectedRows());
		},
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content:
					selectedRows.length > 0
						? `${selectedRows.length} ${t("common:selected", { defaultValue: "\u0432\u044B\u0431\u0440\u0430\u043D\u043E" })}`
						: `${totalRows} ${t("pay_nav_transactions", { defaultValue: "\u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0439" })}`,
				align: "left" as const,
			},
		],
		[totalRows, selectedRows.length, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<>
			{/* Status filter */}
			<div className="flex items-center justify-between p-4 pb-2">
				<h3 className="text-sm font-medium">{t("pay_transactions_title")}</h3>
				<div className="flex items-center gap-2">
					<Select value={statusFilter} onValueChange={setStatusFilter}>
						<SelectTrigger className="w-44">
							<SelectValue placeholder={t("pay_filter_all")} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">{t("pay_filter_all")}</SelectItem>
							{TX_STATUSES.map((s) => (
								<SelectItem key={s} value={s}>
									{t(`pay_status_${s}`)}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Button
						variant="outline"
						size="sm"
						className="h-9"
						onClick={() =>
							gridRef.current?.api?.exportDataAsCsv({
								fileName: "transactions.csv",
							})
						}
					>
						{t("common:export", { defaultValue: "Экспорт" })}
					</Button>
				</div>
			</div>

			<div
				className="flex-1 ag-theme-quartz"
				style={{ width: "100%" }}
			>
				<AgGridReact
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					rowSelection="multiple"
					onSelectionChanged={onSelectionChanged}
					onGridReady={onGridReady}
					onSortChanged={() => { onStateChanged(); refreshGrid(); }}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					suppressRowClickSelection
					animateRows={false}
					rowHeight={28}
					headerHeight={32}
					getRowId={(p) => p.data?.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("pay_transactions_empty", { defaultValue: "\u041D\u0435\u0442 \u0442\u0440\u0430\u043D\u0437\u0430\u043A\u0446\u0438\u0439" })}</p></div>`}
					overlayLoadingTemplate={`<span style="font-size:12px;color:hsl(var(--muted-foreground))">${t("common:loading", { defaultValue: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430" })}...</span>`}
				/>
			</div>

			{/* Capture Dialog */}
			<Dialog
				open={!!captureTarget}
				onOpenChange={(o) => !o && setCaptureTarget(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("pay_capture_title")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("pay_col_amount")}</Label>
							<Input
								type="number"
								value={captureAmount}
								onChange={(e) => setCaptureAmount(Number(e.target.value))}
							/>
							<p className="text-xs text-muted-foreground">
								{t("pay_capture_hint")}
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setCaptureTarget(null)}
						>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => captureMut.mutate()}
							disabled={captureMut.isPending}
						>
							{captureMut.isPending && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("pay_action_capture")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Refund Dialog */}
			<Dialog
				open={!!refundTarget}
				onOpenChange={(o) => !o && setRefundTarget(null)}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("pay_refund_title")}</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label>{t("pay_col_amount")}</Label>
							<Input
								type="number"
								value={refundAmount}
								onChange={(e) => setRefundAmount(Number(e.target.value))}
							/>
						</div>
						<div className="space-y-2">
							<Label>{t("pay_refund_reason")}</Label>
							<Input
								value={refundReason}
								onChange={(e) => setRefundReason(e.target.value)}
								placeholder={t("pay_refund_reason_placeholder")}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setRefundTarget(null)}
						>
							{t("common:cancel")}
						</Button>
						<Button
							onClick={() => refundMut.mutate()}
							disabled={refundMut.isPending}
						>
							{refundMut.isPending && (
								<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
							)}
							{t("pay_action_refund")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
